import { Point, ToolType, TextElement, StickyNote, PathData, Layer, CANVAS_WIDTH, CANVAS_HEIGHT, NOTE_COLORS, NOTE_W, NOTE_H, TRANSPARENT } from './types';
/**
 * Converts raw pointer points into a smooth SVG cubic bezier path
 * using Catmull-Rom spline interpolation.
 * This eliminates the jagged/angular look on iPad/stylus input.
 */
export function pointsToSvgPath(points: Point[]): string {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const p = points[0];
    return `M ${p.x} ${p.y} L ${p.x + 0.1} ${p.y}`;
  }
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  // Catmull-Rom tension factor (0 = sharp, 0.5 = smooth, 1 = very loose)
  const tension = 0.4;

  let d = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(i + 2, points.length - 1)];

    // Catmull-Rom control points
    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;

    d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }

  return d;
}

/**
 * Builds a filled SVG outline path that simulates variable-width brush strokes.
 * For each segment, we compute perpendicular offsets based on per-point widths,
 * then connect them into a closed filled polygon — giving a natural calligraphy feel.
 */
export function buildVariableStrokePath(points: Point[], widths: number[]): string {
  if (points.length < 2) {
    // Single dot
    const p = points[0];
    const r = (widths[0] ?? 2) / 2;
    return `M ${p.x - r} ${p.y} A ${r} ${r} 0 1 0 ${p.x + r} ${p.y} A ${r} ${r} 0 1 0 ${p.x - r} ${p.y} Z`;
  }

  const n = points.length;
  const left: { x: number; y: number }[] = [];
  const right: { x: number; y: number }[] = [];

  for (let i = 0; i < n; i++) {
    const prev = points[Math.max(i - 1, 0)];
    const next = points[Math.min(i + 1, n - 1)];

    // Tangent direction
    let tx = next.x - prev.x;
    let ty = next.y - prev.y;
    const len = Math.sqrt(tx * tx + ty * ty) || 1;
    tx /= len;
    ty /= len;

    // Perpendicular (normal)
    const nx = -ty;
    const ny = tx;

    const hw = (widths[i] ?? widths[widths.length - 1] ?? 2) / 2;

    left.push({ x: points[i].x + nx * hw, y: points[i].y + ny * hw });
    right.push({ x: points[i].x - nx * hw, y: points[i].y - ny * hw });
  }

  // Build smooth outline: left side forward, right side backward
  const tension = 0.35;

  function catmullSegments(pts: { x: number; y: number }[]): string {
    if (pts.length === 0) return '';
    let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(i - 1, 0)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(i + 2, pts.length - 1)];
      const cp1x = p1.x + (p2.x - p0.x) * tension;
      const cp1y = p1.y + (p2.y - p0.y) * tension;
      const cp2x = p2.x - (p3.x - p1.x) * tension;
      const cp2y = p2.y - (p3.y - p1.y) * tension;
      d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
    }
    return d;
  }

  const leftPath = catmullSegments(left);
  const rightReversed = [...right].reverse();
  const rightPath = catmullSegments(rightReversed).replace(/^M/, 'L');

  // Round caps at start and end
  const startR = (widths[0] ?? 2) / 2;
  const endR = (widths[n - 1] ?? 2) / 2;
  const startCap = `A ${startR.toFixed(2)} ${startR.toFixed(2)} 0 0 0 ${left[0].x.toFixed(2)} ${left[0].y.toFixed(2)}`;
  const endCap = `A ${endR.toFixed(2)} ${endR.toFixed(2)} 0 0 0 ${right[n - 1].x.toFixed(2)} ${right[n - 1].y.toFixed(2)}`;

  return `${leftPath} ${endCap} ${rightPath} ${startCap} Z`;
}

export function renderShapeElement(path: PathData): string {
  const { points, color, width, tool, fill, opacity } = path;
  if (points.length < 2) return '';

  const start = points[0];
  const end = points[points.length - 1];
  const minX = Math.min(start.x, end.x);
  const minY = Math.min(start.y, end.y);
  const w = Math.abs(end.x - start.x);
  const h = Math.abs(end.y - start.y);
  const fillAttr = fill && fill !== TRANSPARENT ? `fill="${fill}"` : 'fill="none"';
  const opacityAttr = opacity !== undefined && opacity < 1 ? ` opacity="${opacity}"` : '';

  switch (tool) {
    case 'rectangle':
      return `<rect x="${minX}" y="${minY}" width="${w}" height="${h}" stroke="${color}" stroke-width="${width}" ${fillAttr}${opacityAttr} />`;
    case 'circle': {
      const cx = (start.x + end.x) / 2;
      const cy = (start.y + end.y) / 2;
      const r = Math.max(w, h) / 2;
      return `<circle cx="${cx}" cy="${cy}" r="${r}" stroke="${color}" stroke-width="${width}" ${fillAttr}${opacityAttr} />`;
    }
    case 'ellipse': {
      const ecx = (start.x + end.x) / 2;
      const ecy = (start.y + end.y) / 2;
      const rx = w / 2;
      const ry = h / 2;
      return `<ellipse cx="${ecx}" cy="${ecy}" rx="${rx}" ry="${ry}" stroke="${color}" stroke-width="${width}" ${fillAttr}${opacityAttr} />`;
    }
    case 'triangle': {
      const topX = (start.x + end.x) / 2;
      const topY = Math.min(start.y, end.y);
      const bottomY = Math.max(start.y, end.y);
      const leftX = Math.min(start.x, end.x);
      const rightX = Math.max(start.x, end.x);
      return `<polygon points="${topX},${topY} ${rightX},${bottomY} ${leftX},${bottomY}" stroke="${color}" stroke-width="${width}" ${fillAttr} stroke-linejoin="round"${opacityAttr} />`;
    }
    case 'line':
      return `<line x1="${start.x}" y1="${start.y}" x2="${end.x}" y2="${end.y}" stroke="${color}" stroke-width="${width}" stroke-linecap="round"${opacityAttr} />`;
    default:
      return '';
  }
}

export function renderSvg(layers: Layer[], notes: StickyNote[] = [], textElements: TextElement[] = []): string {
  const layerElements = layers
    .filter((layer) => layer.visible)
    .map((layer) => {
      const elements = layer.paths.map((path) => {
        const op = path.opacity !== undefined && path.opacity < 1 ? ` opacity="${path.opacity}"` : '';
        if (path.tool === 'eraser') {
          const d = pointsToSvgPath(path.points);
          return `<path d="${d}" stroke="#ffffff" stroke-width="${path.width}" fill="none" stroke-linecap="round" stroke-linejoin="round" />`;
        }
        if (path.tool === 'pen') {
          if (path.widths && path.widths.length >= 2 && path.points.length >= 2) {
            const d = buildVariableStrokePath(path.points, path.widths);
            return `<path d="${d}" fill="${path.color}" stroke="none"${op} />`;
          }
          const d = pointsToSvgPath(path.points);
          return `<path d="${d}" stroke="${path.color}" stroke-width="${path.width}" fill="none" stroke-linecap="round" stroke-linejoin="round"${op} />`;
        }
        return renderShapeElement(path);
      }).join('\n');
      return `<g id="${layer.id}">\n${elements}\n</g>`;
    }).join('\n');

  const noteElements = notes.map((note) => {
    const nc = NOTE_COLORS[note.colorIndex] || NOTE_COLORS[0];
    const lines = note.text.split('\n');
    const lineEls = lines.map((line, i) =>
      `<tspan x="${note.noteX + 10}" dy="${i === 0 ? '1.2em' : '1.4em'}">${line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</tspan>`
    ).join('');
    return [
      `<line x1="${note.anchorX}" y1="${note.anchorY}" x2="${note.noteX + NOTE_W / 2}" y2="${note.noteY + NOTE_H}" stroke="${nc.border}" stroke-width="1.5" stroke-dasharray="4 3" />`,
      `<rect x="${note.noteX}" y="${note.noteY}" width="${NOTE_W}" height="${NOTE_H}" rx="6" fill="${nc.bg}" stroke="${nc.border}" stroke-width="1.5" />`,
      `<text x="${note.noteX}" y="${note.noteY}" font-size="11" fill="#1c1917" font-family="sans-serif">${lineEls}</text>`,
    ].join('\n');
  }).join('\n');

  const textEls = textElements.map((t) => {
    const lines = t.text.split('\n');
    const lineEls = lines.map((line, i) => {
      const safe = line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<tspan x="${t.x}" dy="${i === 0 ? 0 : t.fontSize * 1.3}">${safe || ' '}</tspan>`;
    }).join('');
    return `<text x="${t.x}" y="${t.y}" font-size="${t.fontSize}" fill="${t.color}" font-family="${t.fontFamily}">${lineEls}</text>`;
  }).join('\n');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}" width="${CANVAS_WIDTH}" height="${CANVAS_HEIGHT}">\n<rect width="100%" height="100%" fill="white"/>\n${layerElements}\n${noteElements}\n${textEls}\n</svg>`;
}

export function isShapeTool(tool: ToolType): boolean {
  return tool === 'rectangle' || tool === 'circle' || tool === 'ellipse' || tool === 'triangle' || tool === 'line';
}

export function isFillableTool(tool: ToolType): boolean {
  return tool === 'rectangle' || tool === 'circle' || tool === 'ellipse' || tool === 'triangle';
}

export function createDefaultLayer(index: number): Layer {
  return {
    id: `layer-${Date.now()}-${index}`,
    name: `Katman ${index + 1}`,
    visible: true,
    locked: false,
    paths: [],
  };
}
